import type { ComponentType } from 'react';

import type { ForecastForm, IdCampaignPositionDto } from '@/services/ws-digital/src/main/resources/rest-api';
import type { DateType } from '@/js/libs/api-helpers';
import type { CellContentAlign } from '@/js/libs/ui';
import type { ContentTypeBasedCellProps, TableHeadProps } from '@/js/libs/ui/src/V2';

import type { OrderDirection } from './hooks/useSortAndOrder';

export type Option<T> = {
  label: string;
  value: T;
  active?: boolean;
  marked?: boolean;
  locked?: boolean;
  disabled?: boolean;
  description?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyObject = Record<string | number, any>;

/* eslint-disable @typescript-eslint/no-explicit-any */
export type ValidationErrors<FormValues> =
  FormValues extends Partial<Record<any, any>>
    ? {
        [K in keyof FormValues]?: FormValues[K] extends Array<infer T>
          ? Array<ValidationErrors<T>> | string
          : FormValues[K] extends Partial<AnyObject> | undefined
            ? { [KK in keyof FormValues[K]]?: ValidationErrors<FormValues[K][KK]> } | string
            : string;
      }
    : string;
/* eslint-enable @typescript-eslint/no-explicit-any */

export type ValidateFormValues<Value> = (value: Value) => ValidationErrors<Value>;

export type NewOption<T> = Option<T> & { __isNew__: true }; // For creatable options and isNewOption typeGuard

export type TablePropsType = {
  page?: number;
  limit?: number;
  sort?: string;
  order?: string;
};

export type SortParameters<FieldType = string> = {
  sort?: FieldType;
  order?: OrderDirection;
};

export type SortProps = {
  order?: OrderDirection;
  handleChangeOrder?: () => void;
};

export type ExtractProps<TComponentOrTProps> = TComponentOrTProps extends React.ComponentType<infer TProps> ? TProps : TComponentOrTProps;

export enum LanguageKey {
  German = 'de',
  English = 'en',
  French = 'fr',
  Italian = 'it',
}

interface ColumnProps<TName extends string = string> {
  align?: CellContentAlign;
  name: TName;
  title: string;
  sortable?: boolean;
  basis?: 'main' | 'secondary';
}

export type AnyId = string | { toString: () => string };

export interface AnyDto {
  id: AnyId;
}

export interface ColumnComponentProps<TName extends string = string> {
  column: ColumnProps<TName>;
}

export interface Column<TName extends string = string, TProps extends ColumnComponentProps<TName> = ColumnComponentProps<TName>>
  extends ColumnProps<TName> {
  component?: ComponentType<TProps>;
}

export interface SelectAllProps {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  indeterminate: boolean;
}

interface ColumnBasePropsV2<TName extends string = string> extends Omit<TableHeadProps, 'contentType' | 'handleChangeOrder'> {
  name: TName;
  customSortKey?: string;
  hidden?: boolean;
  toggleSelectAll?: SelectAllProps;
}

type ColumnPropsV2<TName extends string = string> = ColumnBasePropsV2<TName> & ContentTypeBasedCellProps;

export interface ColumnComponentPropsV2<TName extends string = string> {
  column: ColumnPropsV2<TName>;
}

export type ColumnV2<
  TName extends string = string,
  TProps extends ColumnComponentPropsV2<TName> = ColumnComponentPropsV2<TName>,
> = ColumnPropsV2<TName> & {
  component?: ComponentType<TProps>;
};

export enum CreativeSearchKey {
  REDIRECT_TO = 'redirectTo',
  LANGUAGE = 'language',
  ACCOUNT_ID = 'accountId',
  NEW_CREATIVE_ID = 'newCreativeId',
  POSITION_ID = 'positionId',
}

export enum PositionHistorySearchKey {
  BACK_TO = 'backTo',
}

export enum CampaignSearchKey {
  ACTIVITY_ID = 'activityId',
}

type Join<TPrevKey, TCurrentKey> = TPrevKey extends string | number
  ? TCurrentKey extends string | number
    ? `${TPrevKey}${'' extends TCurrentKey ? '' : '.'}${TCurrentKey}`
    : never
  : never;

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]];

/**
 * Source: https://stackoverflow.com/a/58436959
 */
export type Paths<TObject, TDepth extends number = 10> = [TDepth] extends [never]
  ? never
  : TObject extends object
    ? {
        [Key in keyof TObject]-?: Key extends string | number ? `${Key}` | Join<Key, Paths<TObject[Key], Prev[TDepth]>> : never;
      }[keyof TObject]
    : '';

export interface DatasetEntry {
  readonly x: string;
  readonly y: number;
}

export type FieldNameMap<TForm extends AnyObject> = Required<{
  [Key in keyof TForm]: Key;
}>;

export type Converter<TFrom, TTo> = (from: TFrom) => TTo;

export enum ErrorCode {
  Unauthorized = 401,
  NotFound = 404,
  Forbidden = 403,
  InvalidForm = 422,
  GatewayTimeout = 504,
}

/**
 * Allows to limit the keys of an object to those that match a given type.
 * https://stackoverflow.com/a/54520829/23268170
 */
export type KeysMatching<T, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

export type RecursiveKeyOf<T> = {
  [K in keyof T & (string | number)]: T[K] extends object ? `${K}` | `${K}.${RecursiveKeyOf<T[K]>}` : `${K}`;
}[keyof T & (string | number)];

export enum ForecastSearchKey {
  SEARCH = 'search',
}

export type ForecastSearchParameters = Omit<
  ForecastForm,
  'startDate' | 'endDate' | 'segmentTargetingIds' | 'contextualTargetingIds' | 'iab3CategoryTargetingIds' | 'sentimentTargetings'
> & {
  startDate?: DateType;
  endDate?: DateType;
  positionId?: IdCampaignPositionDto;
};
